import Vue from 'vue';
import VueRouter from 'vue-router';
import middlewarePipeline from '@app/router/middlewarePipeline';
import NotFound from '@app/pages/errors/NotFound';
import Forbidden from '@app/pages/errors/Forbidden';
import { usePageHeaderStore } from '@services/pinia/PageHeaderStore';
import { useLayoutHandlerStore } from '@services/pinia/LayoutHandlerStore';

Vue.use(VueRouter);

/**
 * Resolve async components.
 *
 * @param  {Array} components
 * @return {Array}
 */
function resolveComponents(components) {
    return Promise.all(components.map((component) => (typeof component === 'function' ? component() : component)));
}

/**
 * Call each middleware.
 *
 * @param {Array} middleware
 * @param {Route} to
 * @param {Route} from
 * @param {Function} next
 */
// eslint-disable-next-line no-shadow
function callMiddleware(middleware, to, from, next) {
    if (!middleware) {
        next();
        return;
    }
    const context = {
        to, from, next,
    };
    middleware[0]({
        ...context,
        next: middlewarePipeline(context, middleware, 1),
    });
}

export default function createRouter(routes, basePath = '') {
    routes.push({
        path: `${basePath}/not-found`,
        name: 'not-found',
        meta: { name: 'Not Found' },
        component: NotFound,
    });
    routes.push({
        path: `${basePath}/forbidden`,
        name: 'forbidden',
        meta: { name: 'Forbidden' },
        component: Forbidden,
    });
    routes.push({
        path: '/:catchAll(.*)',
        redirect: { name: 'not-found' },
    });

    const router = new VueRouter({
        mode: 'history',
        routes,
        scrollBehavior(to, from, savedPosition) {
            if (savedPosition) {
                return savedPosition;
            }
            return { x: 0, y: 0 };
        },
    });

    router.beforeEach(async (to, from, next) => {
        const { middleware } = to.meta;
        const menuTag = (to.meta.menu_tag ? to.meta.menu_tag : '');
        const newTitle = (to.meta && to.meta.name ? to.meta.name : null);

        let components = [];

        try {
            // Get the matched components and resolve them.
            components = await resolveComponents(
                router.getMatchedComponents({ ...to }),
            );
        } catch (error) {
            if (/^Loading( CSS)? chunk (\d)+ failed\./.test(error.message)) {
                window.location.reload(true);
                return;
            }
        }

        if (components.length === 0) {
            next();
            return;
        }

        // Start the loading bar.
        if (components[components.length - 1].loading !== false && router.app.$loading) {
            // router.app.$nextTick(() => router.app.$loading.start())
        }

        callMiddleware(middleware, to, from, (...args) => {
            // Set the application layout only if "next()" was called with no args.
            if (args.length === 0) {
                useLayoutHandlerStore().setActiveTag(menuTag);
                router.app.setLayout(components[0].layout || '');
                usePageHeaderStore().update({ title: newTitle });
                useLayoutHandlerStore().setOverride({ topbar_color: (to.meta && to.meta.topbar_color ? to.meta.topbar_color : null), sidebar_color: (to.meta && to.meta.sidebar_color ? to.meta.sidebar_color : null) });
            }

            next(...args);
        });
    });
    // eslint-disable-next-line no-unused-vars
    router.afterEach(async (to, from) => {
        await router.app.$nextTick();
        if (router.app.$loading) {
            // router.app.$loading.finish()
        }
    });

    return router;
}
